import type * as Blockly from 'blockly/core';
import { defineBlock } from '@/(routers)/boards/editor/block/blockRegistry';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import { FieldOperatorDropdown } from '@/(routers)/boards/editor/block/definitions/fields/fieldOperatorDropdown';
import { FieldStyledBadgeLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledBadgeLabel';
import { FieldStyledLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledLabel';

const TYPE_OPTIONS = [
    { value: 'PRESSURE', label: '気圧', tooltip: '現在地の気圧 (hPa)' },
    { value: 'ALTITUDE', label: '標高', tooltip: '現在地の標高 (m)' },
] as const;

type TypeValue = (typeof TYPE_OPTIONS)[number]['value'];

const BADGE_MAP: Record<TypeValue, string> = {
    PRESSURE: 'hPa',
    ALTITUDE: 'm',
};

const TYPE_COLOURS: Record<TypeValue, string> = {
    PRESSURE: '#cc0000',
    ALTITUDE: '#00cc00',
};

const POSITION_OPTIONS = {
    orange: {
        rectColour: CATEGORIES.sensing.color,
        options: [{ value: 'UPPER_LEFT', label: '左上' }],
    },
    blue: {
        rectColour: CATEGORIES.sensing.color,
        options: [
            { value: 'UPPER_LEFT', label: '左上' },
            { value: 'LOWER_LEFT', label: '左下' },
            { value: 'UPPER_RIGHT', label: '右上' },
            { value: 'LOWER_RIGHT', label: '右下' },
        ],
    },
} as const;

defineBlock({
    type: 'barometric_pressure',
    colour: CATEGORIES.sensing.color,
    textColour: '#FFFFFF',
    tooltip: '気圧センサーの値を読み取ります',
    movable: true,
    deletable: true,
    output: 'Number',
    inputs: [
        {
            kind: 'dummy',
            fields: [
                { type: 'image', icon: { src: '/images/block/barometric_pressure.svg', size: 65 } },
                {
                    type: 'field',
                    instance: () => new FieldStyledLabel('気圧センサー', { fontSize: 15, fontWeight: 'bold' }),
                },
                {
                    type: 'field',
                    name: 'BADGE',
                    instance: () => new FieldStyledBadgeLabel('hPa'),
                },
                { type: 'label', text: ' ' },
                {
                    type: 'dropdown',
                    name: 'POSITION',
                    rectColour: POSITION_OPTIONS.orange.rectColour,
                    options: [...POSITION_OPTIONS.orange.options],
                },
                { type: 'label', text: 'で' },
                {
                    type: 'field',
                    name: 'TYPE',
                    instance: () => {
                        const field = new FieldOperatorDropdown(
                            TYPE_OPTIONS as unknown as { value: string; label: string }[],
                        );
                        field.setRectColour(TYPE_COLOURS['PRESSURE']);
                        field.setOnValueChange((value: string) => {
                            field.setRectColour(TYPE_COLOURS[value as TypeValue]);
                        });
                        return field;
                    },
                },
                { type: 'label', text: 'を計測する ' },
            ],
        },
    ],
    onBoardChange: (block, board) => {
        const field = block.getField('POSITION') as FieldOperatorDropdown | null;
        if (!field) return;
        field.setOptions([...POSITION_OPTIONS[board].options]);
        if (board === 'orange') field.setValue('UPPER_LEFT');
        (block as Blockly.BlockSvg).render();
    },
    onFieldChange: (block, fieldName, newValue) => {
        if (fieldName !== 'TYPE') return;
        const badge = block.getField('BADGE') as FieldStyledBadgeLabel | null;
        if (!badge) return;
        badge.setValue(BADGE_MAP[newValue as TypeValue] ?? 'hPa');
    },
});
